import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import Button from '../components/UI/Button';
import VolunteerModal from '../components/Modals/VolunteerModal';
import { volunteersAPI } from '../services/api';

interface Volunteer {
  id: string;
  name: string;
  email: string;
  phone: string;
  status: 'active' | 'inactive' | 'pending';
  joinDate: string;
  totalHours: number;
  maxHoursPerWeek: number;
  skills: string[];
  languages: string[];
  notes?: string;
  // Additional fields for comprehensive volunteer management
  firstName?: string;
  lastName?: string;
  address?: string;
  city?: string;
  county?: string;
  postalCode?: string;
  dateOfBirth?: string;
  occupation?: string;
  motivation?: string;
  previousExperience?: string;
  preferredActivities?: string[];
  availability: string[];
  lastActivity: string;
  emergencyContact: string;
  emergencyContactRelation?: string;
  medicalConditions?: string;
  hasTransport?: boolean;
  canWorkWeekends?: boolean;
}

const Volunteers: React.FC = () => {
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingVolunteer, setEditingVolunteer] = useState<Volunteer | null>(null);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 10;

  // Load volunteers from API with pagination
  const loadVolunteers = async (page: number = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: itemsPerPage,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter })
      };
      
      const response = await volunteersAPI.getVolunteers(params);
      if (response.success) {
        setVolunteers(response.data.data || []);
        setCurrentPage(response.data.pagination?.page || 1);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setTotalCount(response.data.pagination?.total || 0);
      } else {
        console.error('Failed to load volunteers:', response.message);
      }
    } catch (error) {
      console.error('Error loading volunteers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVolunteers();
  }, []);

  // Reload when status filter changes (not search term)
  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filtering
    loadVolunteers(1);
  }, [statusFilter]);

  // Search function triggered by Enter key
  const handleSearch = () => {
    setCurrentPage(1);
    loadVolunteers(1);
  };

  // Handle Enter key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePageChange = (page: number) => {
    loadVolunteers(page);
  };

  const handleAddVolunteer = () => {
    setEditingVolunteer(null);
    setModalMode('add');
    setShowModal(true);
  };

  const handleEditVolunteer = (volunteer: Volunteer) => {
    setEditingVolunteer(volunteer);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleSaveVolunteer = async (volunteerData: Partial<Volunteer>, hoursToAdd?: { hours: number; activity: string; description?: string; date: string }) => {
    try {
      
      // Sanitize numeric fields
      const sanitizedData = {
        ...volunteerData,
        maxHoursPerWeek: volunteerData.maxHoursPerWeek ? Number(volunteerData.maxHoursPerWeek) : undefined,
        totalHours: volunteerData.totalHours ? Number(volunteerData.totalHours) : undefined,
      };
      
      if (modalMode === 'add') {
        const response = await volunteersAPI.createVolunteer(sanitizedData);
        if (response.success) {
          const newVolunteer = response.data;
          setVolunteers(prev => [...prev, newVolunteer]);
        } else {
          console.error('Failed to create volunteer:', response.message);
          alert('Eroare la crearea voluntarului');
        }
      } else {
        // First update the volunteer
        const response = await volunteersAPI.updateVolunteer(editingVolunteer!.id, sanitizedData);
        if (response.success) {
          let updatedVolunteer = response.data;
          
          // If hours are being added, add them
          if (hoursToAdd) {
            const hoursResponse = await volunteersAPI.addVolunteerHours(editingVolunteer!.id, hoursToAdd);
            if (hoursResponse.success) {
              // Update the volunteer's total hours locally
              const currentTotal = parseFloat(updatedVolunteer.totalHours) || 0;
              updatedVolunteer = {
                ...updatedVolunteer,
                totalHours: currentTotal + hoursToAdd.hours
              };
              alert(`Voluntar actualizat și ${hoursToAdd.hours} ore adăugate cu succes!`);
            } else {
              console.error('Failed to add hours:', hoursResponse.message);
              alert('Voluntar actualizat, dar a apărut o eroare la adăugarea orelor');
            }
          }
          
          setVolunteers(prev => prev.map(v => 
            v.id === editingVolunteer?.id ? updatedVolunteer : v
          ));
        } else {
          console.error('Failed to update volunteer:', response.message);
          alert('Eroare la actualizarea voluntarului');
        }
      }
      setShowModal(false);
    } catch (error) {
      console.error('Error saving volunteer:', error);
      alert('Eroare la salvarea voluntarului');
    }
  };

  const handleDeleteVolunteer = async (id: string) => {
    if (window.confirm('Ești sigur că vrei să ștergi acest voluntar?')) {
      try {
        await volunteersAPI.deleteVolunteer(id);
        setVolunteers(volunteers.filter(v => v.id !== id));
      } catch (error) {
        console.error('Error deleting volunteer:', error);
        alert('Eroare la ștergerea voluntarului');
      }
    }
  };



  // Use server-side filtered data directly
  const displayedVolunteers = volunteers;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Activ';
      case 'inactive':
        return 'Inactiv';
      case 'pending':
        return 'În așteptare';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Voluntari</h1>
            <p className="text-gray-600">Gestionează voluntarii adăpostului</p>
          </div>
          <Button variant="primary" onClick={handleAddVolunteer}>
            Adaugă voluntar
          </Button>
        </div>



        {/* Filters */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Caută
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  placeholder="Caută după nume sau email... (apasă Enter)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button
                  onClick={handleSearch}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  Caută
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">Toți</option>
                <option value="active">Activi</option>
                <option value="inactive">Inactivi</option>
                <option value="pending">În așteptare</option>
              </select>
            </div>
          </div>
        </div>

        {/* Volunteers List */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Voluntar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ore
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Abilități
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acțiuni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayedVolunteers.map((volunteer) => (
                  <tr key={volunteer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {volunteer.name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          Membru din {volunteer.joinDate ? new Date(volunteer.joinDate).toLocaleDateString('ro-RO') : 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{volunteer.email || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{volunteer.phone || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(volunteer.status)}`}>
                        {getStatusText(volunteer.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>{volunteer.totalHours || 0} ore total</div>
                      <div className="text-gray-500">{volunteer.maxHoursPerWeek || 0} ore/săptămână</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {(volunteer.skills || []).map((skill, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-dark"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditVolunteer(volunteer)}
                          className="text-primary hover:text-primary-dark"
                        >
                          Editează
                        </button>
                        <button
                          onClick={() => handleDeleteVolunteer(volunteer.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Șterge
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination Controls */}
        {!loading && totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between sm:px-6 rounded-lg shadow">
            <div className="flex-1 flex items-center justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  currentPage === 1
                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                    : 'text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                Anterior
              </button>
              <div className="flex items-center">
                <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full">
                  {currentPage}/{totalPages}
                </span>
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  currentPage === totalPages
                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                    : 'text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                Următor
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Afișare{' '}
                  <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> la{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, totalCount)}
                  </span>{' '}
                  din <span className="font-medium">{totalCount}</span> voluntari
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                      currentPage === 1
                        ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                        : 'text-gray-500 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">Previous</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === currentPage
                            ? 'z-10 bg-primary text-white border-primary'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                      currentPage === totalPages
                        ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                        : 'text-gray-500 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">Next</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <VolunteerModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSave={handleSaveVolunteer}
          volunteer={editingVolunteer}
          mode={modalMode}
        />
      )}


    </Layout>
  );
};

export default Volunteers; 